import { ChangeEventHandler, useState } from 'react'

import { isEqual } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'
import { connect, useDataItem } from 'scrivito'

import { InvoiceSublistItem } from './InvoiceSublistItem'
import { useInvoiceTabData } from './useInvoiceTabData'
import { LIST_SIZE } from '../../const'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Search } from '@/Atomic/Molecules/Inputs/Search'
import { Option } from '@/Atomic/Molecules/Inputs/Select/types'
import { SimpleSelect } from '@/Atomic/Molecules/SimpleSelect'
import { ScrollablePanel } from '@/Atomic/Templates/ScrollablePanel'
import { Sublist, SublistFooter } from '@/Atomic/Templates/Sublist'
import { DataItemInvoiceDrawer } from '@/Atomic/Widgets/DataItemDrawer'
import {
  DataClassName,
  INVOICE_ASC_ORDER,
  INVOICE_DESC_ORDER,
} from '@/Data/const'
import { DataScopeOrder } from '@/Data/types'
import { useFlashSublistItem } from '@/hooks/useFlashSublistItem'
import { useDataItemDrawer } from '@/utils/CRUD/hooks/useDataItemDrawer'
import { getEntityLabelKey } from '@/utils/getEntityLabelKey'

interface InvoiceTabProps {
  searchInputName: string
  subjectRelation: string
}

export const InvoiceTab: React.FC<InvoiceTabProps> = connect(
  ({ searchInputName, subjectRelation }) => {
    const { t } = useTranslation()
    const dataItem = useDataItem()
    const subjectId = dataItem?.id() ?? ''

    const [search, setSearch] = useState('')
    const [order, setOrder] = useState<DataScopeOrder>(INVOICE_DESC_ORDER)

    const {
      drawerItem,
      setDrawerItem,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      openDrawerId,
    } = useDataItemDrawer()
    const { flashId: flashItemId } = useFlashSublistItem()

    const onSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      setSearch(event.target.value)
    }

    const orderOptions: Option[] = [
      { label: t('common.sort.recentFirst'), value: 'desc' },
      { label: t('common.sort.recentLast'), value: 'asc' },
    ]
    const currentOrderValue = isEqual(order, INVOICE_DESC_ORDER)
      ? 'desc'
      : 'asc'
    const selectedOrderOption = orderOptions.find(
      (o) => o.value === currentOrderValue,
    )
    const handleOrderChange = (newValue: SingleValue<Option | unknown>) => {
      const val = (newValue as Option)?.value
      setOrder(val === 'asc' ? INVOICE_ASC_ORDER : INVOICE_DESC_ORDER)
    }

    const { items, hasMore, loadMore, count } = useInvoiceTabData({
      subjectId,
      subjectRelation,
      search,
      order,
      listSize: LIST_SIZE,
    })

    if (!subjectId) return

    const itemIds = items.map((item) => item.id)
    const currentDrawerId = drawerItem?.id()
    const currentIndex = itemIds.findIndex((id) => id === currentDrawerId)
    const nextId = itemIds[currentIndex + 1]
    const previousId = itemIds[currentIndex - 1]

    const header = (
      <div className="tab-header-wrapper">
        <div className="search-filter">
          <Search
            name={searchInputName}
            onChange={onSearchChange}
            searchWrapperClassName="tab-header-search"
            placeholder={t('search.title')}
          />
          <StringText value={t('common.sortBy')} />
          <SimpleSelect
            name="order-select"
            className="small"
            options={orderOptions}
            value={selectedOrderOption}
            onChange={handleOrderChange}
          />
        </div>
      </div>
    )

    return (
      <>
        <Sublist>
          <ScrollablePanel header={header}>
            {items.map((item) => (
              <InvoiceSublistItem
                key={item.id}
                invoice={item.invoice}
                flash={item.id === flashItemId}
                onOpen={(invoice) => {
                  setDrawerItem(invoice)
                  openDrawer()
                }}
              />
            ))}
            <SublistFooter
              amount={items.length}
              total={count}
              labelKey={getEntityLabelKey(DataClassName.INVOICE)}
              onLoadMore={hasMore ? loadMore : undefined}
            />
          </ScrollablePanel>
        </Sublist>
        {drawerItem && (
          <DataItemInvoiceDrawer
            key={drawerItem.id()}
            isOpen={isDrawerOpen}
            close={closeDrawer}
            dataItem={drawerItem}
            openDrawerId={openDrawerId}
            nextId={nextId}
            previousId={previousId}
          />
        )}
      </>
    )
  },
)
